/**
 * Fetches session details including consent status from the backend.
 * Endpoint: GET /secure/api/sessionDetails
 * Called once (no polling) when entering the chat flow.
 * Uses singleton pattern to prevent duplicate calls.
 * Requires hs_session cookie for authentication.
 */

export interface ConsentStatusResponse {
  isConsentActive: boolean;
  redirectUrl?: string;
  firstName?: string;
}

// Singleton promise to ensure only one API call
let consentPromise: Promise<ConsentStatusResponse> | null = null;

export async function getConsentStatus(
  signal?: AbortSignal,
): Promise<ConsentStatusResponse> {
  // If we already have a pending request, return that promise
  if (consentPromise) {
    return consentPromise;
  }

  // Create new request - using /secure/api/sessionDetails endpoint
  consentPromise = fetch('/secure/api/sessionDetails', {
    signal,
    credentials: 'include', // Includes hs_session cookie
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch session details: ${response.status}`);
      }
      return response.json();
    })
    .catch((error) => {
      // Reset promise on error so it can be retried
      consentPromise = null;
      throw error;
    });

  return consentPromise;
}

// Export function to reset the singleton (useful for testing)
export function resetConsentStatus() {
  consentPromise = null;
}
